import Service from '@ember/service';

export type CsvRow = Record<string, string | number | null | undefined>;

export default class CsvExportService extends Service {
  /**
   * Downloads a CSV file with the given headers and rows
   * @param headers - Array of header strings
   * @param rows - Array of row data (arrays of values or objects with header keys)
   * @param filename - Optional filename (defaults to timestamped filename)
   */
  public downloadCsv(
    headers: string[],
    rows: (string | number | null | undefined)[][] | CsvRow[],
    filename?: string,
  ): void {
    if (!headers.length || !rows.length) {
      return;
    }

    const csvRows: string[] = [
      headers.map((h) => this.escapeCsvValue(h)).join(','),
    ];

    for (const row of rows) {
      let values: (string | number | null | undefined)[];

      if (Array.isArray(row)) {
        values = row;
      } else {
        values = headers.map((header) => row[header]);
      }

      csvRows.push(values.map((value) => this.escapeCsvValue(value)).join(','));
    }

    const csvContent = csvRows.join('\n');
    const blob = new window.Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = window.document.createElement('a');
    const url = window.URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      filename || `export-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    if (
      stringValue.includes(',') ||
      stringValue.includes('\n') ||
      stringValue.includes('"')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }
}

declare module '@ember/service' {
  interface Registry {
    'csv-export': CsvExportService;
  }
}
